<template>
  <div class="repair-dispatch-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairDispatchManage.page.title')" :subtitle="$t('repairDispatchManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input :placeholder="$t('repairDispatchManage.placeholder.repairId')" clearable class="w-full"
            v-model.trim="repairDispatchManageInfo.conditions.repairId" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input :placeholder="$t('repairDispatchManage.placeholder.repairName')" clearable class="w-full"
            v-model.trim="repairDispatchManageInfo.conditions.repairName" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input :placeholder="$t('repairDispatchManage.placeholder.tel')" clearable class="w-full"
            v-model.trim="repairDispatchManageInfo.conditions.tel" @keyup.enter.native="_queryOwnerRepairMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="repairDispatchManageInfo.conditions.repairType" clearable class="w-full"
            :placeholder="$t('repairDispatchManage.placeholder.selectRepairType')">
            <el-option v-for="(item, index) in repairDispatchManageInfo.repairTypes" :key="index"
              :label="item.repairTypeName" :value="item.repairType" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="repairDispatchManageInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('repairDispatchManage.placeholder.selectStatus')">
            <el-option v-for="(item, index) in repairDispatchManageInfo.states" :key="index" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryOwnerRepairMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetOwnerRepairMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listOwnerRepairs">
        <template #left>
          <span class="ds-panel__title">{{ $t('repairDispatchManage.repairBacklog') }}</span>
          <span v-if="repairDispatchManageInfo.conditions.roomName" class="ds-text-muted">
            · {{ repairDispatchManageInfo.conditions.roomName }}
          </span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="repairDispatchManageInfo.ownerRepairs" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('repairDispatchManage.page.empty')"
        @retry="_listOwnerRepairs">
        <el-table-column prop="repairObjName" :label="$t('repairDispatchManage.table.location')" align="center" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('repairDispatchManage.table.repairType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairName" :label="$t('repairDispatchManage.table.repairPerson')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.repairName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="tel" :label="$t('repairDispatchManage.table.repairPhone')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('repairDispatchManage.table.appointmentTime')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('repairDispatchManage.table.status')" align="center" width="140">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
              :type="getStateType(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" min-width="280" fixed="right">
          <template slot-scope="scope">
            <el-button-group>
              <el-button
                v-if="(scope.row.state == '1100' || scope.row.state == '1200' || scope.row.state == '1300') && scope.row.state != '2001'"
                size="mini" @click="_openDispatchRepairModel(scope.row)">
                {{ $t('repairDispatchManage.transferOrder') }}
              </el-button>
              <el-button
                v-if="scope.row.preStaffId != '-1' && scope.row.ruId != scope.row.preRuId && scope.row.state != '2001'"
                size="mini" @click="_openBackRepairModel(scope.row)">
                {{ $t('repairDispatchManage.returnOrder') }}
              </el-button>
              <el-button
                v-if="(scope.row.state == '1100' || scope.row.state == '1200' || scope.row.state == '1300') && scope.row.state != '2001'"
                size="mini" @click="_openFinishRepair(scope.row)">
                {{ $t('repairDispatchManage.complete') }}
              </el-button>
              <el-button
                v-if="hasPrivilege('502021040151320003') && scope.row.state == '1800' && scope.row.returnVisitFlag == '003' && scope.row.state != '2001'"
                size="mini" @click="_openAppraiseRepair(scope.row)">
                {{ $t('repairDispatchManage.returnVisit') }}
              </el-button>
              <el-button v-if="scope.row.state == '1100' || scope.row.state == '1200' || scope.row.state == '1300'"
                size="mini" @click="_openStopRepair(scope.row)">
                {{ $t('repairDispatchManage.pause') }}
              </el-button>
              <el-button v-if="scope.row.state == '2001'" size="mini" @click="_openStartRepair(scope.row)">
                {{ $t('repairDispatchManage.start') }}
              </el-button>
              <el-button size="mini" @click="_openDispatchRepairDetail(scope.row)">
                {{ $t('common.detail') }}
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <dispatch-repair ref="dispatchRepair" @success="handleSuccess" />
    <appraise-repair ref="appraiseRepair" @success="handleSuccess" />
    <stop-repair ref="stopRepair" @success="handleSuccess" />
    <start-repair ref="startRepair" @success="handleSuccess" />
  </div>
</template>

<script>
import { listStaffRepairs, listRepairSettings } from '@/api/work/repairDispatchManageApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import DispatchRepair from '@/components/work/dispatchRepair'
import AppraiseRepair from '@/components/work/AppraiseRepair'
import StopRepair from '@/components/work/StopRepair'
import StartRepair from '@/components/work/StartRepair'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'RepairDispatchManageList',
  components: {
    DispatchRepair,
    AppraiseRepair,
    StopRepair,
    StartRepair,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      repairDispatchManageInfo: {
        ownerRepairs: [],
        total: 0,
        records: 1,
        moreCondition: false,
        repairTypes: [],
        states: [],
        conditions: {
          repairId: '',
          repairName: '',
          tel: '',
          repairType: '',
          roomId: '',
          roomName: '',
          ownerId: '',
          userId: '',
          userName: '',
          state: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._initData()
  },
  methods: {
    async _initData() {
      try {
        // 获取报修类型字典
        this.repairDispatchManageInfo.states = await getDict('r_repair_pool', 'state')
        this._listRepairTypes()
        this._listOwnerRepairs()
      } catch (error) {
        console.error('初始化数据失败:', error)
        this.$message.error(this.$t('common.initFailed'))
      }
    },
    async _listOwnerRepairs() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.repairDispatchManageInfo.conditions,
          page: this.page.current,
          row: this.page.size,
          communityId: this.communityId
        }

        const res = await listStaffRepairs(params)
        this.repairDispatchManageInfo.ownerRepairs = res.data
        this.page.total = res.total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('repairDispatchManage.fetchRepairListFailed')
        console.error('获取报修列表失败:', error)
        this.$message.error(this.$t('repairDispatchManage.fetchRepairListFailed'))
      } finally {
        this.loading = false
      }
    },
    async _listRepairTypes() {
      try {
        const params = {
          page: 1,
          row: 50,
          communityId: this.communityId
        }

        const res = await listRepairSettings(params)
        this.repairDispatchManageInfo.repairTypes = res.data
      } catch (error) {
        console.error('获取报修类型失败:', error)
        this.$message.error(this.$t('repairDispatchManage.fetchRepairTypesFailed'))
      }
    },
    _queryOwnerRepairMethod() {
      this.page.current = 1
      this._listOwnerRepairs()
    },
    _resetOwnerRepairMethod() {
      this.repairDispatchManageInfo.conditions.repairId = ''
      this.repairDispatchManageInfo.conditions.repairName = ''
      this.repairDispatchManageInfo.conditions.tel = ''
      this.repairDispatchManageInfo.conditions.repairType = ''
      this.repairDispatchManageInfo.conditions.state = ''
      this.page.current = 1
      this._listOwnerRepairs()
    },
    getStateType(state) {
      switch (state) {
        case '2001': return 'muted'
        case '1800': return 'warning'
        case '1900': return 'success'
        default: return 'info'
      }
    },
    _openDispatchRepairModel(row) {
      this.$refs.dispatchRepair.open({ ...row, action: 'TRANSFER' })
    },
    _openBackRepairModel(row) {
      this.$refs.dispatchRepair.open({ ...row, action: 'BACK' })
    },
    _openFinishRepair(row) {
      this.$router.push({
        path: '/views/work/finishRepair',
        query: {
          repairType: row.repairType,
          repairId: row.repairId,
          repairObjType: row.repairObjType,
          publicArea: row.publicArea,
          repairChannel: row.repairChannel
        }
      })
    },
    _openDispatchRepairDetail(row) {
      this.$router.push({
        path: '/views/work/repairDetail',
        query: { repairId: row.repairId }
      })
    },
    _openAppraiseRepair(row) {
      this.$refs.appraiseRepair.open(row)
    },
    _openStopRepair(row) {
      this.$refs.stopRepair.open(row)
    },
    _openStartRepair(row) {
      this.$refs.startRepair.open(row)
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this._listOwnerRepairs()
    },
    handleCurrentChange(current) {
      this.page.current = current
      this._listOwnerRepairs()
    },
    handleSuccess() {
      this._listOwnerRepairs()
    }
  }
}
</script>

<style lang="scss" scoped>
.repair-dispatch-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
